import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Select, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import MobileEmailPreview from '../MobileEmailPreview';

import './styles.scss';

const MOCK_SENDERS = [
    {
        label: 'lisa_matthew@cglobal.com',
        value: '1',
        subLabel: 'Lisa Matthew',
    },
    {
        label: 'marketing@sage.com',
        value: '2',
        subLabel: 'Marketing Team',
    },
    {
        label: 'sales@company.com',
        value: '3',
        subLabel: 'Sales Department',
    },
];

const SenderForm = memo(
    ({
        senders = MOCK_SENDERS,
        selectedSender = null,
        selectedSenderId: controlledSenderId,
        senderName: controlledSenderName,
        onSave,
        onCancel,
        onAddNewSender,
        onSelectedSenderChange,
        onSenderNameChange,
    }) => {
        const theme = useTheme();

        // Determine if we're in controlled mode (new props provided) or uncontrolled mode (legacy)
        const isControlled = controlledSenderId !== undefined || controlledSenderName !== undefined;

        // Internal state for uncontrolled mode
        const [internalSelectedSenderId, setInternalSelectedSenderId] = useState(
            selectedSender?.value || '',
        );
        const [internalName, setInternalName] = useState(controlledSenderName);

        // Sync with selectedSender prop in uncontrolled mode
        useEffect(() => {
            if (!isControlled) {
                setInternalSelectedSenderId(selectedSender?.value || '');
                setInternalName(controlledSenderName);
            }
        }, [selectedSender, controlledSenderName, isControlled]);

        // Use controlled values when in controlled mode, internal state otherwise
        const selectedSenderId = isControlled ? controlledSenderId : internalSelectedSenderId;
        const name = isControlled ? controlledSenderName : internalName;

        const senderOptions = useMemo(() => senders, [senders]);

        const selectActions = useMemo(
            () => [
                {
                    iconType: 'addPlus',
                    label: getLiteral('action_add_a_new_sender'),
                    onClick: onAddNewSender,
                },
            ],
            [onAddNewSender],
        );

        const selectedSenderValue = useMemo(() => {
            if (!selectedSenderId) return null;
            return senderOptions.find((opt) => opt.value === selectedSenderId) || null;
        }, [senderOptions, selectedSenderId]);

        const handleSenderChange = useCallback(
            (option) => {
                const newValue = option?.value || '';
                if (!isControlled) {
                    setInternalSelectedSenderId(newValue);
                }
                onSelectedSenderChange?.(newValue);
            },
            [isControlled, onSelectedSenderChange],
        );

        const handleNameChange = useCallback(
            (value) => {
                if (!isControlled) {
                    setInternalName(value);
                }
                onSenderNameChange?.(value);
            },
            [isControlled, onSenderNameChange],
        );

        const handleCancel = useCallback(() => {
            onCancel?.();
        }, [onCancel]);

        const handleSave = useCallback(() => {
            onSave?.({
                senderId: selectedSenderId,
                name: name.trim(),
            });
        }, [onSave, selectedSenderId, name]);

        const isValid = useMemo(() => {
            return selectedSenderId && name.trim().length > 0;
        }, [selectedSenderId, name]);

        return (
            <div className="sender-form">
                <div className="sender-form__content">
                    <div className="sender-form__form">
                        <Select
                            label={getLiteral('label_email_address')}
                            isFullWidth
                            options={senderOptions}
                            value={selectedSenderValue}
                            onChange={handleSenderChange}
                            placeholder={getLiteral(
                                'placeholder_marketing_campaigns_select_sender',
                            )}
                            actions={selectActions}
                        />
                        <Input
                            label={getLiteral('label_name')}
                            isFullWidth
                            value={name}
                            onChange={handleNameChange}
                            placeholder={getLiteral(
                                'placeholder_marketing_campaigns_add_name_to_display',
                            )}
                        />
                    </div>
                    <div className="sender-form__preview">
                        <MobileEmailPreview
                            title="Inbox"
                            name={name}
                            message="Message subject..."
                            previewText="Preview text"
                        />
                        <Text
                            type="caption"
                            color={theme.colors.textLight.secondary}
                            className="sender-form__preview-note"
                        >
                            {getLiteral('label_email_preview_may_vary')}
                        </Text>
                    </div>
                </div>
                <div className="sender-form__actions">
                    <Button type="secondary" size="small" onClick={handleCancel}>
                        {getLiteral('action_cancel')}
                    </Button>
                    <Button type="primary" size="small" onClick={handleSave} isDisabled={!isValid}>
                        {getLiteral('action_save')}
                    </Button>
                </div>
            </div>
        );
    },
);

SenderForm.displayName = 'SenderForm';

SenderForm.propTypes = {
    senders: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
            subLabel: PropTypes.string,
        }),
    ),
    selectedSender: PropTypes.shape({
        value: PropTypes.string,
        subLabel: PropTypes.string,
    }),
    selectedSenderId: PropTypes.string,
    senderName: PropTypes.string,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    onAddNewSender: PropTypes.func,
    onSelectedSenderChange: PropTypes.func,
    onSenderNameChange: PropTypes.func,
};

export default SenderForm;
